import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ScriptProgress } from '../lib/api.js';
import { useScriptRun } from '../lib/useScriptRun.js';
import { useScriptSubmission } from '../lib/useScriptSubmission.js';
import { Icon } from '../components/Icon.js';
import {
  DuplicateScriptNotice,
  IntakeMethodSelector,
  ScriptPasteEditor,
  ScriptRefusal,
  pastedTextProblem,
  type IntakeMethod,
} from '../components/ScriptIntake.js';
import { ScriptDropZone } from '../components/ScriptDropZone.js';
import { ManualRequirementsPanel, neededRequirements } from '../components/ManualRequirementsPanel.js';
import { ScriptPrivacyNotice } from '../components/ScriptPrivacyNotice.js';

/**
 * WHERE A PRODUCTION BEGINS — step 1, "Add your script".
 *
 * One heading, one sentence, one choice of three, and only the input that
 * choice needs. The page used to explain the product around the form — what a
 * screenplay becomes, what happens next, an illustration — and the form began
 * below the fold on a laptop.
 *
 * FOUR STATES, NEVER TWO AT ONCE (owner brief, 2026-10-29):
 *
 *   - uploading — the file's own row with its byte progress;
 *   - processing — the screenplay is in and the worker is reading it into
 *     scenes: an indeterminate sweep, because the parser reports no fraction;
 *   - ready — a success card with the real scene count and ONE primary action,
 *     "Review N scenes";
 *   - failed — what went wrong in the producer's terms and the way to retry.
 *
 * Reading a screenplay into scenes is the deterministic parser, not a model,
 * and nothing here calls it AI.
 *
 * THE AI BREAKDOWN IS NOT OFFERED HERE ANY MORE. It was a second red button
 * beside "Review scenes" on the ready state; it belongs after the scenes have
 * been reviewed, where step 2 offers it (`AnalyseWithAi`, `review-scenes-analyse`).
 * Nothing on this page asks for a paid model request.
 *
 * THE THREE WAYS IN lead to different places, and that is the point: a file or
 * pasted text is a screenplay and goes on to its scenes; "Add manually" is for a
 * production with no screenplay — a commercial, a music video — where scenes and
 * a breakdown would be steps about a document that does not exist. Its way on is
 * "Find locations": the workspace's Locations tab, where New Location Search
 * sends the same producer. Setup stays unfinished, like Skip, so a screenplay
 * added later still finds the guided path.
 *
 * ONE WAY OUT. "Skip for now" is in this page's action bar, so the shell draws
 * none above it (`SetupFlow`'s `stepOwnsExit`).
 */

/** How far the screenplay on the production has been read into scenes. */
export type ReadingState =
  | { kind: 'reading' }
  | { kind: 'read'; sceneCount: number }
  | { kind: 'failed'; code: string | null };

/**
 * Scenes are the proof; the PARSE stage says why there are none. A failed parse
 * is said as such — it used to read "Reading the screenplay…" for as long as
 * the tab stayed open, because only the scene count was being watched.
 */
export function readingState(sceneCount: number, progress: ScriptProgress | null): ReadingState {
  if (sceneCount > 0) return { kind: 'read', sceneCount };

  const parse = progress?.stages.find((s) => s.stage === 'PARSE');
  if (parse?.status === 'FAILED') return { kind: 'failed', code: parse.failureCode ?? progress?.failureCode ?? null };

  return { kind: 'reading' };
}

/** Parse failures the worker does not retry: the same text gives the same answer. */
const FINAL_PARSE_FAILURES: ReadonlySet<string> = new Set(['NO_SCENES_FOUND', 'NO_TEXT']);

export function scenesLabel(n: number): string {
  return `${String(n)} scene${n === 1 ? '' : 's'}`;
}

interface PrimaryAction {
  testId: string;
  label: string;
  arrow: boolean;
  disabled: boolean;
  /** Said beside a disabled button: what it is waiting for. */
  hint: string | null;
  onClick?: () => void;
}

export function StartProductionPage({
  projectId,
  onContinue,
  onSkip,
  onFindLocations,
}: {
  projectId: string;
  /** To step 2, once the screenplay has been read into scenes. */
  onContinue: () => void;
  /** Opens the workspace and records nothing on the production. */
  onSkip: () => void;
  /** No screenplay: on to the workspace's Locations tab with what was added by hand. */
  onFindLocations: () => void;
}) {
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<IntakeMethod>('UPLOAD');
  // Lifted to the page so switching to another method and back keeps it.
  const [pasted, setPasted] = useState('');
  const [replacing, setReplacing] = useState(false);
  const stopPolling = useRef(false);

  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
    // Parsing runs on the worker, so `sceneCount` is 0 for a moment after a
    // screenplay lands. Polled until it is not — or until the parse has failed
    // in a way no retry changes.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.sceneCount > 0 || stopPolling.current) return false;

      return 1500;
    },
  });

  const script = current.data ?? null;
  const run = useScriptRun(script?.scriptVersionId ?? null);
  const reading = script === null ? null : readingState(script.sceneCount, run.progress);
  stopPolling.current = reading?.kind === 'failed' && FINAL_PARSE_FAILURES.has(reading.code ?? '');

  const requirements = useQuery({
    queryKey: ['requirements', projectId],
    queryFn: () => api.listRequirements(projectId),
    enabled: method === 'MANUAL',
  });
  const neededCount = neededRequirements(requirements.data ?? []).length;

  /**
   * THE ROUTE DECIDES ON THE SETUP ANSWER, so the answer must be current
   * before the route is asked. A screenplay landing here is what turns
   * `nextStep` from "start" into "scenes" (and ticks step 1 on the tracker);
   * navigating on the old answer sent "Review scenes" straight back here.
   */
  const refreshSetup = () => queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] });

  const submission = useScriptSubmission(projectId, () => {
    // Stay. The producer is watching what they just handed over be read;
    // moving them mid-parse would take the answer away as it arrives.
    setReplacing(false);
    setPasted('');
    void current.refetch();
    void refreshSetup();
  });
  const busy = submission.uploading || submission.pasting;

  const choose = (next: IntakeMethod) => {
    if (next === method) return;
    // The selector is only on screen with a screenplay in while replacing it or
    // adding by hand, so picking a screenplay input then means "use this instead".
    if (script !== null && next !== 'MANUAL') setReplacing(true);
    // A refusal belongs to the input it was for.
    if (!busy) submission.reset();
    setMethod(next);
  };

  const replace = () => {
    submission.reset();
    setReplacing(true);
    if (method === 'MANUAL') setMethod('UPLOAD');
  };

  // The screenplay that is in, in place of an input: processing, ready or failed.
  const showSummary = script !== null && reading !== null && !replacing && method !== 'MANUAL';

  const primary = ((): PrimaryAction => {
    if (method === 'MANUAL' && reading?.kind !== 'read' && reading?.kind !== 'reading') {
      return {
        testId: 'start-find-locations',
        label: 'Find locations',
        arrow: true,
        disabled: neededCount === 0,
        hint: neededCount === 0 ? 'Add a location to continue' : null,
        onClick: onFindLocations,
      };
    }

    if (method === 'PASTE' && !showSummary) {
      const empty = pasted.trim().length === 0;

      return {
        testId: 'script-paste-submit',
        label: submission.pasting ? 'Reading…' : 'Use this text',
        arrow: false,
        disabled: empty || submission.pasting || pastedTextProblem(pasted) !== null,
        hint: empty ? 'Paste some text to continue' : null,
        onClick: () => {
          submission.paste(pasted);
        },
      };
    }

    if (method === 'UPLOAD' && !showSummary) {
      return {
        testId: 'start-continue',
        label: submission.uploading ? 'Uploading…' : 'Review scenes',
        arrow: !submission.uploading,
        disabled: true,
        hint: submission.uploading ? null : 'Add a file to continue',
      };
    }

    // A screenplay is on the production. Only scenes open the next step:
    // pressing it before a scene exists would open an empty table.
    return reading?.kind === 'read'
      ? {
          testId: 'start-continue',
          label: `Review ${scenesLabel(reading.sceneCount)}`,
          arrow: true,
          disabled: false,
          hint: null,
          onClick: () => {
            void refreshSetup().then(onContinue);
          },
        }
      : {
          testId: 'start-continue',
          label: 'Review scenes',
          arrow: true,
          disabled: true,
          hint: reading?.kind === 'failed' ? 'Replace the file to continue' : null,
        };
  })();

  return (
    <div data-testid="start-production-page" className="start-page">
      <h1 className="dh start-title">Add your script</h1>
      <p className="start-lede">
        Upload a screenplay or describe what you need. Review the scenes, run an AI breakdown when
        ready, and explore matching real locations.
      </p>

      <div className="start-card" data-testid="start-card" data-state={showSummary ? reading.kind : 'input'}>
        {showSummary ? null : <IntakeMethodSelector value={method} onChange={choose} disabled={busy} />}

        <div className="start-input" data-testid="start-input" data-method={method}>
          {replacing && script !== null && method !== 'MANUAL' ? (
            <p className="start-replacing" data-testid="start-replacing">
              <span>
                Replacing <strong>{script.title}</strong>. The new one becomes the current version;
                nothing is deleted.
              </span>
              <button
                type="button"
                className="intake-linkish"
                data-testid="start-keep-current"
                disabled={busy}
                onClick={() => {
                  setReplacing(false);
                  submission.reset();
                }}
              >
                Keep the current one
              </button>
            </p>
          ) : null}

          {showSummary ? (
            reading.kind === 'read' ? (
              <ReadyCard title={script.title} sceneCount={reading.sceneCount} onReplace={replace} />
            ) : reading.kind === 'reading' ? (
              <ProcessingCard title={script.title} onReplace={replace} />
            ) : (
              <FailedCard
                title={script.title}
                code={reading.code}
                onReplace={replace}
                onAddManually={() => {
                  setMethod('MANUAL');
                }}
              />
            )
          ) : method === 'MANUAL' ? (
            <ManualRequirementsPanel projectId={projectId} requirements={requirements.data ?? []} />
          ) : method === 'UPLOAD' ? (
            <ScriptDropZone
              busy={submission.uploading}
              file={submission.uploadingFile}
              progress={submission.progress}
              onFile={submission.upload}
              onCancel={submission.cancelUpload}
            />
          ) : (
            <ScriptPasteEditor value={pasted} onChange={setPasted} disabled={submission.pasting} />
          )}

          {method !== 'MANUAL' && submission.refusal ? (
            <ScriptRefusal refusal={submission.refusal} onRetry={submission.retryUpload} />
          ) : null}
          {showSummary && submission.duplicate ? <DuplicateScriptNotice /> : null}
        </div>

        <ScriptPrivacyNotice variant="compact" />

        <div className="start-actions" data-testid="start-actions">
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="setup-skip"
            title="Opens the project. You can continue setup at any time."
            onClick={onSkip}
          >
            Skip for now
          </button>
          <div className="start-primary">
            {primary.disabled && primary.hint !== null ? (
              <span className="start-primary-hint" id="start-primary-hint" data-testid="start-primary-hint">
                {primary.hint}
              </span>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              data-testid={primary.testId}
              disabled={primary.disabled}
              aria-describedby={primary.disabled && primary.hint !== null ? 'start-primary-hint' : undefined}
              onClick={primary.onClick}
            >
              {primary.label}
              {primary.arrow ? <Icon name="arrowRight" size={16} strokeWidth={2} /> : null}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * READY: the screenplay was read into scenes. A success mark, the title, the
 * real count from the parser, and Replace as a text action — the one primary
 * action ("Review N scenes") is the page's own, in the action bar below.
 */
function ReadyCard({ title, sceneCount, onReplace }: { title: string; sceneCount: number; onReplace: () => void }) {
  return (
    <div className="ready-card" data-testid="start-submitted" data-reading="read">
      <span className="ready-mark" aria-hidden="true">
        <Icon name="check" size={20} strokeWidth={2.6} />
      </span>
      <div className="ready-body">
        <h2 className="ready-title" data-testid="start-ready-title">
          Your screenplay is ready
        </h2>
        <p className="ready-script" data-testid="start-script-title">
          <Icon name="script" size={15} />
          <span>{title}</span>
        </p>
        <p className="ready-text" data-testid="start-ready-text">
          {`We've successfully processed your script and identified ${scenesLabel(sceneCount)}. Review ${sceneCount === 1 ? 'it' : 'them'} and make any corrections before continuing to the location breakdown.`}
        </p>
        <button type="button" className="intake-linkish" data-testid="script-replace" onClick={onReplace}>
          Replace file
        </button>
      </div>
      <p className="ready-count" data-testid="start-scene-count" aria-hidden="true">
        <span className="ready-count-n">{sceneCount}</span>
        <span className="ready-count-l">{sceneCount === 1 ? 'scene' : 'scenes'}</span>
      </p>
    </div>
  );
}

/**
 * PROCESSING: the worker is reading the screenplay into scenes. An
 * indeterminate sweep, because the parser reports no fraction — a bar creeping
 * toward 100% would be a number nobody measured.
 */
function ProcessingCard({ title, onReplace }: { title: string; onReplace: () => void }) {
  return (
    <div className="script-file" data-testid="start-submitted" data-reading="reading" aria-busy="true">
      <span className="script-file-icon" aria-hidden="true">
        <Icon name="script" size={20} />
      </span>
      <div className="script-file-body">
        <div className="script-file-head">
          <span className="script-file-name" data-testid="start-script-title">
            {title}
          </span>
          <button type="button" className="intake-linkish" data-testid="script-replace" onClick={onReplace}>
            Replace file
          </button>
        </div>
        <div className="sa-sweep-track script-file-bar" aria-hidden="true">
          <span className="sa-sweep-bar" />
        </div>
        <p className="script-file-meta" role="status" data-testid="start-reading">
          Finding the scenes in your screenplay…
        </p>
      </div>
    </div>
  );
}

/** Why a screenplay that landed was not read into scenes, in the producer's terms. */
export function parseFailure(code: string | null): { title: string; body: string } {
  if (code === 'NO_SCENES_FOUND') {
    return {
      title: 'No scenes were found in it',
      body:
        'It was read, but no scene heading (INT. / EXT.) was recognised. A brief or shot list ' +
        'without them cannot be split into scenes — add the locations you need by hand, or try ' +
        'the screenplay itself.',
    };
  }
  if (code === 'NO_TEXT') {
    return {
      title: 'No readable text in this file',
      body: 'Try another copy of the screenplay, or paste its text instead.',
    };
  }

  return {
    title: 'This file could not be read into scenes',
    body: 'Nothing else is affected. Try another file, or paste the text instead.',
  };
}

/**
 * FAILED: the screenplay is stored but no scene came out of it. There is no
 * re-parse to offer — the same bytes give the same answer, and uploading them
 * again is recognised as the same screenplay — so the retry is another file,
 * or, for a brief with no scene headings, naming the locations by hand.
 */
function FailedCard({
  title,
  code,
  onReplace,
  onAddManually,
}: {
  title: string;
  code: string | null;
  onReplace: () => void;
  onAddManually: () => void;
}) {
  const failure = parseFailure(code);

  return (
    <div className="failed-card" data-testid="start-submitted" data-reading="failed" role="alert">
      <span className="failed-mark" aria-hidden="true">
        !
      </span>
      <div className="ready-body">
        <h2 className="failed-title" data-testid="start-parse-failed">
          {failure.title}
        </h2>
        <p className="ready-script" data-testid="start-script-title">
          <Icon name="script" size={15} />
          <span>{title}</span>
        </p>
        <p className="ready-text">{failure.body}</p>
        <div className="failed-actions">
          <button type="button" className="btn btn-ghost" data-testid="script-replace" onClick={onReplace}>
            Try another file
          </button>
          {code === 'NO_SCENES_FOUND' ? (
            <button type="button" className="btn btn-ghost" data-testid="start-parse-failed-manual" onClick={onAddManually}>
              Add locations manually
            </button>
          ) : null}
        </div>
        {code === null || code === 'NO_SCENES_FOUND' || code === 'NO_TEXT' ? null : (
          <p className="mm" style={{ margin: '8px 0 0', fontSize: 11.5 }}>
            {code}
          </p>
        )}
      </div>
    </div>
  );
}
